import { Link as RouterLink } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import { layoutTokens, typographyTokens } from "@/shared/theme";
import type { PatternFocus } from "../patterns.api";
import { TrackedPatternCard } from "./TrackedPatternCard";

interface TrackedPatternsProps {
  readonly value: PatternFocus;
  /** Opens a tactical pattern's evidence on the Patterns page. */
  readonly onSelect?: (key: string) => void;
  /** The pattern open in the evidence panel, marked VIEWING. */
  readonly selectedKey?: string | null;
  /** Keys that have evidence to open; other cards are not clickable. */
  readonly selectableKeys?: ReadonlySet<string>;
  /** Heading level context: the Focus page names the section itself. */
  readonly showFocusLink?: boolean;
}

/** Every proven pattern Chessla tracks in this format; the study Focus is chosen, never opted into. */
export const TrackedPatterns = ({
  value,
  onSelect,
  selectedKey = null,
  selectableKeys,
  showFocusLink = true,
}: TrackedPatternsProps) => (
  <Box component="section" aria-label="Tracked patterns" sx={{ mt: layoutTokens.sectionGap }}>
    <Box
      sx={{ alignItems: "center", display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "space-between", mb: 1 }}
    >
      <Typography
        sx={{
          color: "text.secondary",
          fontSize: "caption.fontSize",
          fontWeight: "fontWeightBold",
          letterSpacing: typographyTokens.tracking.label,
        }}
      >
        TRACKING {value.tracked.length} {value.cohort.toUpperCase()}{" "}
        {value.tracked.length === 1 ? "PATTERN" : "PATTERNS"}
      </Typography>
      {showFocusLink && value.focus && (
        <Button component={RouterLink} to={`/focus?format=${value.cohort}`} size="small" variant="contained">
          Open Focus
        </Button>
      )}
    </Box>
    {value.tracked.length === 0 ? (
      <Typography color="text.secondary" sx={{ border: layoutTokens.dividerBorder, borderColor: "divider", p: 2 }}>
        Tracking starts automatically once a pattern appears in at least three of your {value.cohort} games.
      </Typography>
    ) : (
      <Box
        sx={{
          display: "grid",
          gap: layoutTokens.contentGap,
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {value.tracked.map((pattern) => (
          <TrackedPatternCard
            key={pattern.id}
            pattern={pattern}
            cohort={value.cohort}
            isFocus={pattern.id === value.focus?.id}
            weeks={value.progress?.find((entry) => entry.key === pattern.key)?.weeks}
            viewing={pattern.key != null && pattern.key === selectedKey}
            onOpen={
              onSelect &&
              pattern.type === "tactical" &&
              pattern.key &&
              (!selectableKeys || selectableKeys.has(pattern.key))
                ? () => onSelect(pattern.key!)
                : undefined
            }
          />
        ))}
      </Box>
    )}
  </Box>
);
